import React, { useContext, useRef } from 'react'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { CreationToolsContext } from '../../../manager'
import CreationTool from '../../../types/creation-tool'
import CreationToolError from '../../../types/error'
import CreationToolFileType from '../../../types/file-type'
import { validateSourceByOutput } from '../../../utils/validate-source'
import { CreationToolUploadContext } from '../manager'
import Bulk from './bulk'

const TRANSPARENT_PIXEL =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

type Props = {
    setIsProcessing?: (isProcessing: boolean) => void
}

const CreationUploadList = ({ setIsProcessing }: Props) => {
    const { trackEvent, tool } = useContext(CreationToolsContext)
    const { uploadSources, updateUploadSource } = useContext(CreationToolUploadContext)
    const numberOfSources = useRef(0)
    // validate upload sources
    useAsyncEffect(async () => {
        // only run this if it's the first time or we have a new upload source
        if (uploadSources.length === numberOfSources.current) {
            return
        }
        numberOfSources.current = uploadSources.length
        setIsProcessing && setIsProcessing(true)
        for (let i = 0; i < uploadSources.length; i++) {
            const uploadSource = uploadSources[i]
            const { source: src, outputType } = uploadSource

            if (src.type === CreationToolFileType.UNVALIDATED_FILE) {
                try {
                    const validatedSource = await validateSourceByOutput(src.source, outputType)
                    updateUploadSource({ ...uploadSource, source: validatedSource })
                } catch (e) {
                    const isFile = typeof uploadSource.source !== 'string'
                    const error = e as CreationToolError
                    const errorAction = CreationToolError.gaAction(error, outputType)
                    const gaCategory = CreationTool.gaEventCategory(tool, uploadSources.length > 1)

                    // if the error is a file too long and this is bulk upload,
                    // we don't want to try creating a preview blob of a huge file.
                    if (
                        isFile &&
                        (error === CreationToolError.VideoTooLong || error === CreationToolError.FileSizeTooLarge)
                    ) {
                        const updateSourceFile = { ...src, source: TRANSPARENT_PIXEL }
                        const file = src.source as File
                        updateUploadSource({
                            ...uploadSource,
                            source: updateSourceFile,
                            title: file.name,
                            error,
                        })

                        trackEvent(gaCategory, errorAction, file.type, CreationToolError.gaValue(error, file))
                    } else if (isFile) {
                        const file = src.source as File
                        updateUploadSource({ ...uploadSource, error })
                        trackEvent(gaCategory, errorAction, file.type)
                    } else {
                        updateUploadSource({ ...uploadSource, error })
                        trackEvent(gaCategory, errorAction, 'not a file')
                    }
                }
            }
        }
        setIsProcessing && setIsProcessing(false)
    }, [uploadSources])
    return <Bulk />
}

export default CreationUploadList
